import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { registerNotificationHandler } from "libs/context/notificationBridge";

const NotificationContext = createContext();

const CATEGORY_ICON_MAP = {
  Candidate: "fa-user",
  AI: "fa-microchip",
  Client: "fa-building",
  General: "fa-bell",
};

const normalizeCategory = (value = "") => {
  const raw = String(value || "").trim().toLowerCase();
  if (raw.includes("candidate")) return "Candidate";
  if (raw === "ai" || raw.includes("recruiter")) return "AI";
  if (raw.includes("client")) return "Client";
  return "General";
};

export const normalizeIncomingNotification = (payload = {}) => {
  const category = normalizeCategory(
    payload?.category ?? payload?.type ?? payload?.notificationType
  );

  return {
    id:
      payload?.id ??
      payload?._id ??
      `notif-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    title: payload?.title ?? payload?.subject ?? "New Notification",
    message: payload?.message ?? payload?.body ?? payload?.description ?? "",
    category,
    icon: CATEGORY_ICON_MAP[category] || CATEGORY_ICON_MAP.General,
    createdAt: payload?.createdAt ?? new Date().toISOString(),
    read: false,
    success: payload?.success !== false,
  };
};

export const formatNotificationTime = (isoDate) => {
  if (!isoDate) return "Just now";

  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return "Just now";

  const diffMs = Date.now() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);

  if (diffSec < 60) return "Just now";
  if (diffSec < 3600) {
    const mins = Math.floor(diffSec / 60);
    return `${mins} min${mins === 1 ? "" : "s"} ago`;
  }
  if (diffSec < 86400) {
    const hours = Math.floor(diffSec / 3600);
    return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  }

  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

export const LIVE_ALERT_DURATION_MS = 6000;

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [liveAlerts, setLiveAlerts] = useState([]);

  const unreadCount = useMemo(
    () => notifications.filter((item) => !item.read).length,
    [notifications]
  );

  const dismissLiveAlert = useCallback((alertId) => {
    setLiveAlerts((prev) => prev.filter((item) => item.id !== alertId));
  }, []);

  const addNotification = useCallback(
    (payload) => {
      const normalized = normalizeIncomingNotification(payload);
      if (!normalized.message && !normalized.title) {
        return normalized;
      }

      setNotifications((prev) => [normalized, ...prev]);
      setLiveAlerts((prev) => [normalized, ...prev].slice(0, 4));

      window.setTimeout(() => {
        dismissLiveAlert(normalized.id);
      }, LIVE_ALERT_DURATION_MS);

      return normalized;
    },
    [dismissLiveAlert]
  );

  const markAllAsRead = useCallback(() => {
    setNotifications((prev) => prev.map((item) => ({ ...item, read: true })));
  }, []);

  const markAsRead = useCallback((notificationId) => {
    setNotifications((prev) =>
      prev.map((item) =>
        item.id === notificationId ? { ...item, read: true } : item
      )
    );
  }, []);

  useEffect(() => {
    registerNotificationHandler(addNotification);
    return () => registerNotificationHandler(null);
  }, [addNotification]);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        liveAlerts,
        unreadCount,
        addNotification,
        dismissLiveAlert,
        markAllAsRead,
        markAsRead,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotificationContext = () => useContext(NotificationContext);
